/**
 * Emergency-contact numbers.
 *
 * OPTIONAL, AND ANY NUMBER (owner's direction, 2 Oct 2026). The contact used
 * to be mandatory and Indian-mobile-only (rule §10.6). Now a member may leave
 * it blank, and may give a landline, a short local number or a foreign number
 * — the person to call is not always on an Indian mobile. What still holds:
 * whatever IS typed must be dialable, and it must not be the member's own
 * number. An Indian mobile is still stored in E.164 like every other number;
 * anything else is stored as its digits (with the + if one was typed).
 *
 * Rule §10.6 makes the emergency contact mandatory on every form that creates
 * a member — but "mandatory" is not enough. The field exists to reach someone
 * ELSE when the runner cannot answer, and two inputs defeat that silently:
 *
 *   - the member's own number  ("same as my contact number")
 *   - text that cannot be dialled  ("not-a-number")
 *
 * Both were accepted by all four forms before this module existed (QA finding
 * F-5, 12 Aug 2026). Every form that collects an emergency number — join
 * request, admin add/edit member, profile edit, join onboarding — now runs it
 * through here, so the rule cannot drift between forms.
 *
 * The same-number check applies to juniors too: their registered mobile may
 * be a parent's handset, but the club already HAS that number — the emergency
 * contact must add a second reachable person. The guardian number is the one
 * place a shared family phone is legitimate, so `guardianPhoneError` checks
 * format only.
 */

import { normalizePhone, samePhone } from './phone';

/** What each denial means to the person filling the form. */
export const EMERGENCY_PHONE_MESSAGES = {
  NOT_A_NUMBER:
    'That emergency number cannot be dialled. Enter a phone number — mobile, landline or international — or leave it blank.',
  SAME_AS_MEMBER:
    'The emergency number must belong to someone else. The club already has this number — if you cannot answer, this contact is the person we call.',
};

/** A phone number has at least this many digits (short local landlines)… */
const MIN_DIGITS = 6;
/** …and at most this many (the E.164 ceiling). */
const MAX_DIGITS = 15;

/**
 * The stored form of an emergency number, or null when there is nothing to
 * store or it cannot be dialled. An Indian mobile (or anything normalizePhone
 * understands) comes back in E.164; any other dialable number comes back as
 * its digits, keeping a leading + when one was typed.
 */
export function normalizeEmergencyPhone(input) {
  if (!input || !String(input).trim()) return null;
  const raw = String(input).trim();
  // Digits and phone punctuation only — 'ask my husband' is not a number.
  if (/[^\d\s+().-]/.test(raw)) return null;
  const e164 = normalizePhone(raw);
  if (e164) return e164;
  const digits = raw.replace(/\D/g, '');
  if (digits.length < MIN_DIGITS || digits.length > MAX_DIGITS) return null;
  return raw.startsWith('+') ? `+${digits}` : digits;
}

/**
 * Why an emergency number is not acceptable, or null when it is fine —
 * including when it is blank, because the contact is optional.
 * Returns 'NOT_A_NUMBER' | 'SAME_AS_MEMBER' | null.
 */
export function emergencyPhoneDenial(input, ownPhone) {
  if (!input || !input.trim()) return null;
  const stored = normalizeEmergencyPhone(input);
  if (!stored) return 'NOT_A_NUMBER';
  if (ownPhone && samePhone(stored, ownPhone)) return 'SAME_AS_MEMBER';
  return null;
}

/** The message a form shows for that number, or null when it is fine. */
export function emergencyPhoneError(input, ownPhone) {
  const denial = emergencyPhoneDenial(input, ownPhone);
  return denial ? EMERGENCY_PHONE_MESSAGES[denial] : null;
}

/**
 * Guardian numbers are optional, and a family phone shared with the junior
 * member is legitimate — so only the format is enforced, and only when a
 * value was actually given.
 */
export function guardianPhoneError(input) {
  if (!input || !input.trim()) return null;
  if (!normalizePhone(input)) {
    return 'That guardian number cannot be dialled. Enter a real mobile number, or leave it blank.';
  }
  return null;
}
